import { useEffect, useMemo, useRef, useState } from "react";
import { Grid, Card, Icon, CircularProgress, InputLabel, LinearProgress, Tab, Tabs, Tooltip } from "@mui/material";
import MDBox from "components/MDBox";
import MDPeriodo from "components/MDPeriodo";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDSelect from "components/MDSelect";
import { get, getFile } from "utils/WebService";
import { errorAlert, successAlert } from "utils/Functions";
import { useMaterialUIController } from "context";
import Painel from "./analiseTempo/Painel";
import KpiCard from "./analiseTempo/KpiCard";
import GraficoEvolucao from "./analiseTempo/GraficoEvolucao";
import GraficoDistribuicao from "./analiseTempo/GraficoDistribuicao";
import GraficoEsforco from "./analiseTempo/GraficoEsforco";
import TabelaDesempenho from "./analiseTempo/TabelaDesempenho";
import DetalheCliente from "./analiseTempo/DetalheCliente";
import AcordosRecebimentos from "./analiseTempo/AcordosRecebimentos";
import CasosDialog from "./analiseTempo/CasosDialog";
import {
    agruparPor, corAcordos, derivados, filtrarLinhas, formatarMoedaCentavos, somarLinhas, totaisPorMes,
} from "./analiseTempo/acordosUtils";
import {
    calcularIndicadores, clientesDosDados, coresPorPessoa, corMedida, formatarNumero, formatarPercentualCurto, formatarPeriodo,
    numerosDaPessoa, periodoAnterior, periodoPadrao, validarPeriodo, variacaoPercentual,
} from "./analiseTempo/analiseUtils";
import { ESCURO } from "assets/coresTema";

const formatoVariacao = new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1, signDisplay: "always" });
const variacaoCard = (variacao) => (variacao === null || variacao === undefined || !Number.isFinite(variacao)
    ? null
    : { texto: `${formatoVariacao.format(variacao * 100)}%`, positiva: variacao >= 0 });
const variacaoPontos = (atual, anterior) => (atual === null || atual === undefined || anterior === null || anterior === undefined
    ? null
    : { texto: `${formatoVariacao.format((atual - anterior) * 100)} p.p.`, positiva: atual >= anterior });

// Mesmas abas de análise da planilha exportada; cada uma com atendimento, acordos e recebimentos juntos
const VISOES = [
    {
        value: "negociadores",
        campoAcordo: "negociador",
        label: "Análise Negociadores",
        rotuloPessoa: "Negociador",
        rotuloPessoas: "negociadores",
        ajuda: "Só usuários com perfil do tipo Membros, Operação ou BackOffice (Ferramentas > Perfis > Tipo de Perfil), inclusive inativos: coordenadores e supervisores ficam na aba de Supervisão; suporte, administrador, gestor, importação e conta sem perfil não entram em nada (acionamentos, acordos e recebimentos). Conta do legado vale pelo perfil da conta atual, se unificada, ou pelo perfil dado a ela. E só quem pertence à carteira da ficha: membro ou backoffice de uma equipe ativa dela; usuário inativo entra pelo que fez nas fichas da carteira (carteira sem equipe ativa: todos). Fichas acionadas e atendimentos de cada negociador; acordos e recebimentos de quem formalizou o acordo.",
    },
    {
        value: "coordenadores",
        campoAcordo: "coordenador",
        label: "Análise Supervisão (Coordenador)",
        rotuloPessoa: "Coordenador",
        rotuloPessoas: "coordenadores",
        ajuda: "O coordenador não é contado pelos próprios acionamentos: cada ficha acionada pela operação e pelo backoffice (e cada acordo deles) conta para o coordenador da equipe da carteira dessa ficha. Coordenadores e supervisores de equipe não entram como quem acionou.",
    },
];

const OPCAO_TODAS_CARTEIRAS = { id: "__todas__", value: "", label: "Todas" };
// Cliente, pessoa e status aceitam vários; nenhum escolhido = todos

// Rótulo acima do campo: o mesmo InputLabel do Período (padrão das telas), para os campos ficarem na mesma linha
const rotuloFiltro = { px: 1, pb: 0.5, pt: 0.2 };

/** Mesma conta para o período atual e o anterior: atendimento (fichas, atendimentos) e acordos no escopo da tela. */
function resumoDoEscopo(dados, dadosAcordos, blocos, campo, pessoasSel, clientesSel) {
    const indicadores = calcularIndicadores(dados, blocos, pessoasSel, clientesSel);
    if (!indicadores) return null;
    const linhas = filtrarLinhas(
        dadosAcordos?.linhas, clientesSel, campo === "negociador" ? pessoasSel : null, campo === "coordenador" ? pessoasSel : null
    );
    const acordos = somarLinhas(linhas);
    return { indicadores, linhas, acordos, ...derivados(acordos, indicadores.fichas) };
}

export default function AnaliseTempoSemestral() {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    const [dataInicial, setDataInicial] = useState(() => periodoPadrao().dataInicial);
    const [dataFinal, setDataFinal] = useState(() => periodoPadrao().dataFinal);
    const [abaAtual, setAbaAtual] = useState(0);
    const visao = VISOES[abaAtual];
    const [pessoas, setPessoas] = useState([]);
    const [clientes, setClientes] = useState([]);
    const [carteirasDisponiveis, setCarteirasDisponiveis] = useState([]);
    const [restritoEquipe, setRestritoEquipe] = useState(false);
    const [restritoCliente, setRestritoCliente] = useState(false);
    const [carteira, setCarteira] = useState(OPCAO_TODAS_CARTEIRAS);
    const [statusOptions, setStatusOptions] = useState([]);
    const [status, setStatus] = useState([]);
    const [dados, setDados] = useState(null);
    const [dadosAnterior, setDadosAnterior] = useState(null);
    const [dadosAcordos, setDadosAcordos] = useState(null);
    const [dadosAcordosAnterior, setDadosAcordosAnterior] = useState(null);
    const [loadingConsulta, setLoadingConsulta] = useState(false);
    const [loading, setLoading] = useState(false);
    const [clientesDetalhe, setClientesDetalhe] = useState([]);
    // "Ver casos" de um card (Fichas Acionadas, Negociações, Valor Recebido): { tipo: fichas | acordos | pagamentos, titulo }
    const [casos, setCasos] = useState(null);
    const requisicaoAtual = useRef(0);
    const cacheConsultas = useRef(new Map());
    // Botão Atualizar: a próxima consulta ignora o cache da tela e pede ao backend que descarte o dele (atualizar=true)
    const [atualizacao, setAtualizacao] = useState(0);
    const forcarAtualizacao = useRef(false);
    const topoRef = useRef(null);

    useEffect(() => {
        get("/relatorios/analise-tempo/carteiras")
            .then(res => {
                setCarteirasDisponiveis(Array.isArray(res?.carteiras) ? res.carteiras : []);
                setRestritoEquipe(!!res?.restritoEquipe);
                setRestritoCliente(!!res?.restritoCliente);
            })
            .catch(err => {
                console.error("Erro ao carregar carteiras da análise:", err);
                setCarteirasDisponiveis([]);
            });
        // Status que o usuário vê (os das suas equipes, os sem equipe e os globais)
        get("/status-ficha/list-all")
            .then(res => {
                const lista = res?.list || res?.data || (Array.isArray(res) ? res : []);
                setStatusOptions(lista
                    .filter(s => s?.id && s?.ccNome)
                    .map(s => ({ id: s.id, value: s.id, label: s.ccNome }))
                    .sort((a, b) => a.label.localeCompare(b.label)));
            })
            .catch(() => setStatusOptions([]));
    }, []);

    const carteirasOptions = useMemo(
        () => [
            OPCAO_TODAS_CARTEIRAS,
            ...carteirasDisponiveis.map(c => ({ id: c.id, value: c.id, label: c.ativo ? c.nome : `${c.nome} (inativa)` })),
        ],
        [carteirasDisponiveis]
    );
    // Usuário de equipe com uma só carteira: o filtro já vem com ela e não muda
    const carteiraTravada = restritoEquipe && carteirasDisponiveis.length === 1 ? carteirasOptions[1] : null;
    useEffect(() => {
        if (carteiraTravada && carteira?.value !== carteiraTravada.value) setCarteira(carteiraTravada);
    }, [carteiraTravada, carteira]);

    // Cliente e pessoa filtram só a tela; período, carteira e status vão para o backend (e para a exportação).
    // Enquanto a data está sendo digitada (ou o período é inválido) não consulta.
    const erroPeriodo = validarPeriodo(dataInicial, dataFinal);
    const filtrosValidos = !erroPeriodo;
    const filtrosBackend = [
        carteira?.value ? `carteiras=${carteira.value}` : null,
        ...status.map(s => `statusFicha=${encodeURIComponent(s.value)}`),
    ].filter(Boolean);
    const parametrosConsulta = [`dataInicial=${dataInicial}`, `dataFinal=${dataFinal}`, ...filtrosBackend].join("&");

    // Mesmos filtros, período anterior equivalente: só para as variações dos cards
    const anterior = filtrosValidos ? periodoAnterior(dataInicial, dataFinal) : null;
    const parametrosAnterior = anterior
        ? [`dataInicial=${anterior.dataInicial}`, `dataFinal=${anterior.dataFinal}`, ...filtrosBackend].join("&")
        : "";

    useEffect(() => {
        if (!filtrosValidos) return undefined;
        // Só a resposta da última troca de filtro vale; as anteriores são descartadas
        const idRequisicao = ++requisicaoAtual.current;
        const vigente = () => idRequisicao === requisicaoAtual.current;
        // As consultas são pesadas: reaproveita períodos já carregados nesta tela (menos no Atualizar)
        const forcar = forcarAtualizacao.current;
        forcarAtualizacao.current = false;
        const buscar = (caminho, parametros, ignorarCache) => {
            const cache = cacheConsultas.current;
            const chave = `${caminho}?${parametros}`;
            if (!ignorarCache && cache.has(chave)) return Promise.resolve(cache.get(chave));
            return get(ignorarCache ? `${chave}&atualizar=true` : chave).then(res => {
                cache.set(chave, res);
                return res;
            });
        };
        const buscarAmbos = (parametros, ignorarCache = false) => Promise.all([
            buscar("/relatorios/analise-tempo", parametros, ignorarCache),
            buscar("/relatorios/analise-tempo/acordos", parametros, ignorarCache),
        ]);

        setLoadingConsulta(true);
        // Espera o usuário terminar de trocar os filtros antes de consultar, para não empilhar consultas no banco
        const espera = setTimeout(() => {
            buscarAmbos(parametrosConsulta, forcar)
                .then(([atendimento, acordos]) => {
                    if (!vigente()) return;
                    setDados(atendimento);
                    setDadosAcordos(acordos);
                    setLoadingConsulta(false);
                    // Período anterior (variações dos cards) só depois do atual, para não disputar o banco
                    setDadosAnterior(null);
                    setDadosAcordosAnterior(null);
                    buscarAmbos(parametrosAnterior)
                        .then(([atendimentoAnt, acordosAnt]) => {
                            if (!vigente()) return;
                            setDadosAnterior(atendimentoAnt);
                            setDadosAcordosAnterior(acordosAnt);
                        })
                        .catch(err => console.error("Sem comparação com o período anterior:", err));
                })
                .catch(err => {
                    if (!vigente()) return;
                    console.error(err);
                    setDados(null);
                    setDadosAcordos(null);
                    setLoadingConsulta(false);
                    errorAlert(err?.message || "Erro ao carregar a Análise de Atendimento.");
                });
        }, 400);
        return () => clearTimeout(espera);
    }, [parametrosConsulta, parametrosAnterior, filtrosValidos, atualizacao]);

    const handleAtualizar = () => {
        forcarAtualizacao.current = true;
        setAtualizacao(n => n + 1);
    };
    // Hora em que os dados da tela foram lidos do banco (o backend guarda o resultado por alguns minutos)
    const geradoEm = [dados?.geradoEm, dadosAcordos?.geradoEm].filter(Boolean);
    const textoGeradoEm = geradoEm.length
        ? new Date(Math.min(...geradoEm)).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }).replace(",", " às")
        : null;

    useEffect(() => {
        setPessoas([]);
    }, [visao.value]);

    const meses = useMemo(() => dados?.meses || [], [dados]);
    const qtdMeses = meses.length;
    const blocos = useMemo(() => (dados && dados[visao.value]) || [], [dados, visao.value]);
    const campo = visao.campoAcordo;
    const pessoasSel = useMemo(() => pessoas.map(p => p.value), [pessoas]);
    const clientesSel = useMemo(() => clientes.map(c => c.value), [clientes]);
    // Clientes do filtro: os que estão nos dados (grupos na carteira de cobrança, o nome na jurídica), que o backend já traz
    // só das carteiras das equipes do usuário e dos clientes liberados; com uma carteira escolhida, só os dela
    const opcoesClientes = useMemo(
        () => clientesDosDados(dados, dadosAcordos?.linhas).map(c => ({ id: c, value: c, label: c })),
        [dados, dadosAcordos]
    );
    // Cliente que saiu dos dados (outra carteira, outro período) sai da escolha
    useEffect(() => {
        if (!dados) return;
        const validos = new Set(opcoesClientes.map(o => o.value));
        setClientes(atuais => (atuais.every(c => validos.has(c.value)) ? atuais : atuais.filter(c => validos.has(c.value))));
    }, [opcoesClientes, dados]);

    // Pessoas nos dados: as que acionaram e as que só formalizaram acordo
    const pessoasDosDados = useMemo(() => {
        const nomes = new Set(blocos.map(b => b.nome));
        (dadosAcordos?.linhas || []).forEach(l => { if (l[campo]) nomes.add(l[campo]); });
        return [...nomes].sort((a, b) => a.localeCompare(b));
    }, [blocos, dadosAcordos, campo]);
    // O filtro lista quem está nos dados: o backend já deixa só quem pertence à carteira de cada ficha, com os
    // usuários inativos pelo que fizeram nela (AnaliseTempoService.soEquipesDaCarteira). Antes o filtro também cortava
    // pela lista das equipes da carteira, e o usuário inativo (que sai da equipe ao ser desligado) não aparecia
    const pessoasOptions = useMemo(
        () => pessoasDosDados.map(nome => ({ id: nome, value: nome, label: nome })),
        [pessoasDosDados]
    );
    useEffect(() => {
        // Pessoa que sumiu dos dados (outro período/carteira) sai do filtro
        if (pessoasSel.some(nome => !pessoasDosDados.includes(nome))) {
            setPessoas(atuais => atuais.filter(p => pessoasDosDados.includes(p.value)));
        }
    }, [pessoasDosDados, pessoasSel]);

    const atual = useMemo(
        () => resumoDoEscopo(dados, dadosAcordos, blocos, campo, pessoasSel, clientesSel),
        [dados, dadosAcordos, blocos, campo, pessoasSel, clientesSel]
    );
    const doAnterior = useMemo(() => {
        const blocosAnterior = (dadosAnterior && dadosAnterior[visao.value]) || [];
        return resumoDoEscopo(dadosAnterior, dadosAcordosAnterior, blocosAnterior, campo, pessoasSel, clientesSel);
    }, [dadosAnterior, dadosAcordosAnterior, visao.value, campo, pessoasSel, clientesSel]);
    const rotuloVariacao = anterior ? `vs ${formatarPeriodo(anterior.dataInicial, anterior.dataFinal)}` : "";

    // Evolução mês a mês: negociações no mês da formalização; conversão do mês = fichas com o 1º acordo no mês ÷ fichas
    // acionadas no mês (a soma dos meses não bate exatamente com a conversão do período)
    const evolucao = useMemo(() => {
        if (!atual) return null;
        const porMes = totaisPorMes(atual.linhas, meses);
        return {
            negociacoesMes: porMes.map(m => m.acordos),
            conversaoMes: porMes.map((m, i) => (atual.indicadores.fichasMes[i] > 0 ? m.fichasConvertidas / atual.indicadores.fichasMes[i] : null)),
        };
    }, [atual, meses]);

    // Mesma cor por pessoa na rosca, no gráfico por cliente e nas tabelas (todas, inclusive quem só formalizou acordo)
    const cores = useMemo(() => coresPorPessoa(blocos, darkMode, pessoasDosDados), [blocos, darkMode, pessoasDosDados]);
    const itensDistribuicao = useMemo(
        () => blocos.map(b => ({ nome: b.nome, valor: numerosDaPessoa(b, clientesSel, qtdMeses).atendimentos })),
        [blocos, clientesSel, qtdMeses]
    );
    const fichasPorCliente = useMemo(() => {
        const mapa = {};
        (dados?.clientes || []).forEach(c => { mapa[c.cliente] = c.fichasPeriodo; });
        return mapa;
    }, [dados]);

    // Desempenho: uma linha por pessoa (todas, a escolhida em destaque) e o total (cada ficha uma vez)
    const desempenho = useMemo(() => {
        if (!dados || !dadosAcordos) return null;
        const linhasCliente = filtrarLinhas(dadosAcordos.linhas, clientesSel);
        const acordosPorPessoa = new Map(agruparPor(linhasCliente.filter(l => l[campo]), campo).map(g => [g.nome, g]));
        const nomes = new Set([...blocos.map(b => b.nome), ...acordosPorPessoa.keys()]);
        const linha = (nome, numeros, acordos) => {
            const d = derivados(acordos, numeros.fichas);
            return {
                nome,
                fichas: numeros.fichas,
                atendimentos: numeros.atendimentos,
                atendimentosMes: numeros.atendimentosMes,
                acordos: acordos.acordos,
                conversao: numeros.fichas ? d.conversao : null,
                valorAcordos: acordos.valorAcordos,
                recebido: d.recebido,
                ticketMedio: d.ticketMedio,
            };
        };
        const zerado = { fichas: 0, atendimentos: 0, atendimentosMes: Array(qtdMeses).fill(0) };
        const linhas = [...nomes]
            .map(nome => {
                const bloco = blocos.find(b => b.nome === nome);
                return linha(nome, bloco ? numerosDaPessoa(bloco, clientesSel, qtdMeses) : zerado, acordosPorPessoa.get(nome) || somarLinhas([]));
            })
            .filter(l => l.fichas || l.atendimentos || l.acordos || l.recebido);
        const todos = calcularIndicadores(dados, blocos, null, clientesSel);
        return { linhas, total: linha("Total Geral", todos || zerado, somarLinhas(linhasCliente)) };
    }, [dados, dadosAcordos, blocos, campo, clientesSel, qtdMeses]);

    // Detalhamento por cliente: os do filtro de cliente ou, sem ele, o de mais fichas acionadas; dá para somar vários
    const clientesComFichas = useMemo(
        () => clientesDosDados(dados).filter(c => (fichasPorCliente[c] || 0) > 0),
        [dados, fichasPorCliente]
    );
    useEffect(() => {
        const doFiltro = clientesSel.filter(c => clientesComFichas.includes(c));
        if (doFiltro.length) {
            setClientesDetalhe(atuais => (atuais.length === doFiltro.length && atuais.every(c => doFiltro.includes(c)) ? atuais : doFiltro));
            return;
        }
        setClientesDetalhe(atuais => {
            const validos = atuais.filter(c => clientesComFichas.includes(c));
            if (validos.length) return validos.length === atuais.length ? atuais : validos;
            const maior = [...clientesComFichas].sort((a, b) => (fichasPorCliente[b] || 0) - (fichasPorCliente[a] || 0))[0];
            return maior ? [maior] : [];
        });
    }, [clientesSel, clientesComFichas, fichasPorCliente]);
    const resumoCliente = useMemo(() => {
        if (!clientesDetalhe.length || !dados) return null;
        // Cada ficha é de um só cliente: somar os clientes não conta ficha duas vezes
        const totais = (dados.clientes || []).filter(c => clientesDetalhe.includes(c.cliente));
        const acordos = somarLinhas((dadosAcordos?.linhas || []).filter(l => clientesDetalhe.includes(l.cliente)));
        const fichas = totais.reduce((acc, c) => acc + (c.fichasPeriodo || 0), 0);
        const d = derivados(acordos, fichas);
        return {
            fichas,
            atendimentos: totais.reduce((acc, c) => acc + (c.atendimentosPeriodo || 0), 0),
            acordos: acordos.acordos,
            conversao: fichas ? d.conversao : null,
            valorAcordos: acordos.valorAcordos,
            recebido: d.recebido,
            ticketMedio: d.ticketMedio,
        };
    }, [clientesDetalhe, dados, dadosAcordos]);

    const selecionarPessoa = (nome) => {
        // O olho da tabela de Desempenho põe a pessoa no filtro (ou tira, se já está); sem nome, volta para todos
        const incluir = nome && !pessoasSel.includes(nome);
        setPessoas(atuais => {
            if (!nome) return [];
            return atuais.some(p => p.value === nome) ? atuais.filter(p => p.value !== nome) : [...atuais, { id: nome, value: nome, label: nome }];
        });
        if (incluir) setTimeout(() => topoRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    };

    const handleExportar = () => {
        if (!filtrosValidos) {
            errorAlert(erroPeriodo);
            return;
        }

        setLoading(true);
        const url = `/relatorios/analise-tempo/exportar?${parametrosConsulta}`;
        // Mesmo nome que o backend gera: Analise_Atendimento_20261001_20261231.xlsx
        const filename = `Analise_Atendimento_${dataInicial.replace(/-/g, "")}_${dataFinal.replace(/-/g, "")}.xlsx`;

        getFile(url, filename)
            .then(() => {
                successAlert("Análise de Atendimento exportada com sucesso!");
                setLoading(false);
            })
            .catch(err => {
                console.error(err);
                errorAlert(err?.message || "Erro ao exportar a Análise de Atendimento.");
                setLoading(false);
            });
    };

    const semDados = !loadingConsulta && (!dados || (blocos.length === 0 && !(dadosAcordos?.linhas || []).length));
    // Na troca de filtro os dados atuais continuam na tela (esmaecidos) até os novos chegarem
    const atualizando = loadingConsulta && !!dados;
    const variacao = (valorAtual, valorAnterior) => variacaoCard(variacaoPercentual(valorAtual, valorAnterior));

    return (
        <Grid container spacing={3}>
            <Grid item xs={12} ref={topoRef}>
                <Card
                    variant="outlined"
                    sx={{
                        p: 2,
                        background: darkMode ? ESCURO.cartao : "#fafafa",
                        borderColor: darkMode ? ESCURO.divisoria : "#ddd",
                    }}
                >
                    <MDBox
                        display="flex"
                        alignItems="center"
                        gap={2}
                        flexWrap="wrap"
                        sx={{ borderBottom: 1, width: "100%", borderColor: darkMode ? "rgba(255, 255, 255, 0.2)" : "divider", mb: 3, pb: 0.5 }}
                    >
                        <MDBox flexGrow={1} minWidth={0}>
                        <Tabs
                            value={abaAtual}
                            onChange={(event, novaAba) => setAbaAtual(novaAba)}
                            aria-label="abas da análise de atendimento"
                            textColor={darkMode ? "inherit" : "primary"}
                            indicatorColor="info"
                        >
                            {VISOES.map((v, idx) => (
                                <Tab
                                    key={v.value}
                                    label={v.label}
                                    sx={{ fontSize: 12, color: darkMode ? (abaAtual === idx ? "#fff" : "rgba(255, 255, 255, 0.7)") : "inherit" }}
                                />
                            ))}
                        </Tabs>
                        </MDBox>
                        <MDBox display="flex" alignItems="center" gap={1.5}>
                            {textoGeradoEm && (
                                <MDTypography variant="caption" color="text" sx={{ whiteSpace: "nowrap", opacity: 0.85 }}>
                                    Dados de {textoGeradoEm}
                                </MDTypography>
                            )}
                            <Tooltip title="Buscar os dados de novo no banco (a tela guarda o resultado por alguns minutos)" placement="top">
                                <span>
                                    <MDButton variant="outlined" color="info" size="small" onClick={handleAtualizar} disabled={loadingConsulta || !filtrosValidos}>
                                        {loadingConsulta
                                            ? <CircularProgress size={14} color="inherit" sx={{ mr: 0.75 }} />
                                            : <Icon sx={{ mr: 0.5 }}>refresh</Icon>}
                                        Atualizar
                                    </MDButton>
                                </span>
                            </Tooltip>
                        </MDBox>
                    </MDBox>

                    <Grid container spacing={2} alignItems="flex-start">
                        <Grid item xs={12} md={6} xl={3}>
                            <MDPeriodo inicio={dataInicial} fim={dataFinal}
                                onChange={({ inicio, fim }) => { setDataInicial(inicio); setDataFinal(fim); }} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3} xl={2}>
                            <InputLabel sx={rotuloFiltro}>Carteira</InputLabel>
                            <MDSelect
                                isMulti={false}
                                name="carteira"
                                value={carteira}
                                options={carteirasOptions}
                                isDisabled={Boolean(carteiraTravada)}
                                onChange={val => setCarteira(val || OPCAO_TODAS_CARTEIRAS)}
                            />
                            {carteiraTravada && (
                                <MDTypography variant="caption" color="text" sx={{ px: 1, display: "block" }}>
                                    Única carteira liberada para você.
                                </MDTypography>
                            )}
                        </Grid>
                        <Grid item xs={12} sm={6} md={3} xl={2}>
                            <InputLabel sx={rotuloFiltro}>Cliente</InputLabel>
                            <MDSelect
                                isMulti
                                name="cliente"
                                placeholder="Todos"
                                value={clientes}
                                options={opcoesClientes}
                                onChange={val => setClientes(val || [])}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} xl={2}>
                            <InputLabel sx={rotuloFiltro}>{visao.rotuloPessoa}</InputLabel>
                            <MDSelect
                                isMulti
                                name="pessoa"
                                placeholder="Todos"
                                value={pessoas}
                                options={pessoasOptions}
                                isDisabled={loadingConsulta || (pessoasOptions.length === 0 && pessoas.length === 0)}
                                onChange={val => setPessoas(val || [])}
                            />
                            <MDTypography variant="caption" color="text" sx={{ px: 1, display: "block" }}>
                                Equipes da carteira e usuários inativos.
                            </MDTypography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} xl={2}>
                            <InputLabel sx={rotuloFiltro}>Status do Atendimento</InputLabel>
                            <MDSelect
                                isMulti
                                name="statusFicha"
                                placeholder="Todos"
                                value={status}
                                options={statusOptions}
                                onChange={val => setStatus(val || [])}
                            />
                        </Grid>
                        <Grid item xs={12} md={4} xl={1}>
                            {/* Rótulo vazio: o botão fica na mesma linha dos campos */}
                            <InputLabel sx={rotuloFiltro}>&nbsp;</InputLabel>
                            <Tooltip title="Exportar a análise para o Excel (.xlsx): período, carteira e status" placement="top">
                                <span style={{ display: "block", width: "100%" }}>
                                    <MDButton variant="gradient" color="info" onClick={handleExportar} disabled={loading} fullWidth sx={{ height: 38, minHeight: 38, px: 1.5 }}>
                                        {loading ? (
                                            <CircularProgress size={18} color="inherit" />
                                        ) : (
                                            <>
                                                <Icon sx={{ mr: 0.75 }}>file_download</Icon>
                                                Exportar
                                            </>
                                        )}
                                    </MDButton>
                                </span>
                            </Tooltip>
                        </Grid>

                        <Grid item xs={12} sx={{ pt: "8px !important" }}>
                            {erroPeriodo && (
                                <MDTypography variant="caption" color="error" fontWeight="bold" sx={{ px: 1, display: "block" }}>
                                    {erroPeriodo}
                                </MDTypography>
                            )}
                            <MDTypography variant="caption" color="text" sx={{ px: 1, display: "block" }}>
                                Período de até 12 meses, um mês por coluna. Status do Atendimento = status de hoje da ficha.
                                Cliente, {visao.rotuloPessoa} e Status aceitam mais de um (em branco = todos). Cliente e {visao.rotuloPessoa} filtram só a
                                tela; a exportação leva período, carteira e status.
                            </MDTypography>
                            {(restritoEquipe || restritoCliente) && (
                                <MDTypography variant="caption" color="text" sx={{ px: 1, display: "block", fontStyle: "italic" }}>
                                    {restritoEquipe && "Você visualiza a base das carteiras das suas equipes. "}
                                    {restritoCliente && "Você visualiza só os clientes liberados para você."}
                                </MDTypography>
                            )}
                        </Grid>
                    </Grid>
                </Card>
            </Grid>

            {loadingConsulta && !dados && (
                <Grid item xs={12}>
                    <MDBox display="flex" justifyContent="center" alignItems="center" py={8}>
                        <CircularProgress size={36} color="info" />
                    </MDBox>
                </Grid>
            )}

            {atualizando && (
                <Grid item xs={12} sx={{ pt: "12px !important" }}>
                    <LinearProgress color="info" sx={{ borderRadius: 1 }} />
                </Grid>
            )}

            {semDados && (
                <Grid item xs={12}>
                    <Painel>
                        <MDTypography variant="caption" color="text" sx={{ display: "block", textAlign: "center", py: 4 }}>
                            Nenhum acionamento, acordo ou pagamento encontrado no período selecionado.
                        </MDTypography>
                    </Painel>
                </Grid>
            )}

            {!semDados && dados && atual && desempenho && evolucao && (
                <Grid item xs={12} sx={{ opacity: atualizando ? 0.5 : 1, transition: "opacity 0.2s", pointerEvents: atualizando ? "none" : "auto" }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6} lg={4} xxl={2}>
                            <KpiCard
                                compacto
                                icone="assignment_turned_in"
                                cor={corMedida("fichas", darkMode)}
                                rotulo="Fichas Acionadas"
                                onClick={() => setCasos({ tipo: "fichas", titulo: "Fichas acionadas" })}
                                dicaClique="Ver as fichas acionadas (com os atendimentos de cada uma)"
                                valor={formatarNumero(atual.indicadores.fichas)}
                                detalhe={pessoasSel.length > 1 ? `soma das fichas de cada ${visao.rotuloPessoa.toLowerCase()}` : "fichas distintas no período"}
                                variacao={variacao(atual.indicadores.fichas, doAnterior?.indicadores.fichas)}
                                rotuloVariacao={rotuloVariacao}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} lg={4} xxl={2}>
                            <KpiCard
                                compacto
                                icone="support_agent"
                                cor={corMedida("atendimentos", darkMode)}
                                rotulo="Atendimentos Realizados"
                                valor={formatarNumero(atual.indicadores.atendimentos)}
                                detalhe="registros de histórico no período"
                                variacao={variacao(atual.indicadores.atendimentos, doAnterior?.indicadores.atendimentos)}
                                rotuloVariacao={rotuloVariacao}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} lg={4} xxl={2}>
                            <KpiCard
                                compacto
                                icone="handshake"
                                cor={corMedida("negociacoes", darkMode)}
                                rotulo="Negociações (Acordos)"
                                onClick={() => setCasos({ tipo: "acordos", titulo: "Negociações (acordos)" })}
                                dicaClique="Ver os acordos (com o valor e se contam na conversão)"
                                valor={formatarNumero(atual.acordos.acordos)}
                                detalhe="acordos formalizados no período"
                                variacao={variacao(atual.acordos.acordos, doAnterior?.acordos.acordos)}
                                rotuloVariacao={rotuloVariacao}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} lg={4} xxl={2}>
                            <KpiCard
                                compacto
                                icone="percent"
                                cor={darkMode ? "#e6e6e6" : "#344767"}
                                rotulo="Conversão"
                                valor={atual.conversao === null ? "–" : formatarPercentualCurto(atual.conversao)}
                                detalhe={`${formatarNumero(atual.acordos.fichasConvertidas)} fichas com acordo`}
                                variacao={variacaoPontos(atual.conversao, doAnterior?.conversao)}
                                rotuloVariacao={rotuloVariacao}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} lg={4} xxl={2}>
                            <KpiCard
                                compacto
                                icone="request_quote"
                                cor={corAcordos("negociado", darkMode)}
                                rotulo="Valor Negociado"
                                valor={formatarMoedaCentavos(atual.acordos.valorAcordos)}
                                detalhe="soma dos acordos formalizados"
                                variacao={variacao(atual.acordos.valorAcordos, doAnterior?.acordos.valorAcordos)}
                                rotuloVariacao={rotuloVariacao}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} lg={4} xxl={2}>
                            <KpiCard
                                compacto
                                icone="payments"
                                cor={corAcordos("recebido", darkMode)}
                                rotulo="Valor Recebido"
                                onClick={() => setCasos({ tipo: "pagamentos", titulo: "Valor recebido (pagamentos)" })}
                                dicaClique="Ver os pagamentos"
                                valor={formatarMoedaCentavos(atual.recebido)}
                                detalhe="pagamentos no período"
                                variacao={variacao(atual.recebido, doAnterior?.recebido)}
                                rotuloVariacao={rotuloVariacao}
                            />
                        </Grid>

                        <Grid item xs={12} xl={5}>
                            <Painel rotulo="Evolução dos Atendimentos">
                                <GraficoEvolucao
                                    meses={meses}
                                    fichasMes={atual.indicadores.fichasMes}
                                    atendimentosMes={atual.indicadores.atendimentosMes}
                                    negociacoesMes={evolucao.negociacoesMes}
                                    conversaoMes={evolucao.conversaoMes}
                                />
                                <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 1, opacity: 0.8 }}>
                                    Conversão do mês: fichas com o 1º acordo no mês ÷ fichas acionadas no mês.
                                </MDTypography>
                            </Painel>
                        </Grid>
                        <Grid item xs={12} md={6} xl={3}>
                            <Painel rotulo={`Distribuição dos Atendimentos por ${visao.rotuloPessoa}`}>
                                <GraficoDistribuicao itens={itensDistribuicao} cores={cores} selecionados={pessoasSel} rotuloCentro="Atendimentos" />
                            </Painel>
                        </Grid>
                        <Grid item xs={12} md={6} xl={4}>
                            <Painel rotulo="Participação nos Atendimentos por Cliente">
                                <GraficoEsforco
                                    blocos={blocos}
                                    cores={cores}
                                    fichasPorCliente={fichasPorCliente}
                                    selecionados={pessoasSel}
                                    clientesDestaque={clientesSel}
                                    rotuloPessoa={visao.rotuloPessoa}
                                    rotuloPessoas={visao.rotuloPessoas}
                                />
                                <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 1, opacity: 0.8 }}>
                                    Cada barra soma 100% das fichas acionadas do cliente no período, divididas entre os {visao.rotuloPessoas}.
                                    Fatia pequena: passe o mouse para ver o %. Clique num nome para destacar.
                                </MDTypography>
                            </Painel>
                        </Grid>

                        <Grid item xs={12}>
                            <Painel>
                                <TabelaDesempenho
                                    titulo={`Desempenho dos ${visao.rotuloPessoas === "negociadores" ? "Negociadores" : "Coordenadores"}`}
                                    linhas={desempenho.linhas}
                                    total={desempenho.total}
                                    meses={meses}
                                    cores={cores}
                                    selecionados={pessoasSel}
                                    onSelecionar={selecionarPessoa}
                                    rotuloPessoa={visao.rotuloPessoa}
                                />
                            </Painel>
                        </Grid>

                        {clientesDetalhe.length > 0 && (
                            <Grid item xs={12}>
                                <DetalheCliente
                                    blocos={blocos}
                                    meses={meses}
                                    clientes={clientesComFichas}
                                    selecionados={clientesDetalhe}
                                    onSelecionados={setClientesDetalhe}
                                    resumo={resumoCliente}
                                    pessoasSelecionadas={pessoasSel}
                                    cores={cores}
                                    rotuloPessoa={visao.rotuloPessoa}
                                />
                            </Grid>
                        )}

                        <Grid item xs={12}>
                            <MDTypography variant="button" fontWeight="bold" color="text" sx={{ display: "block", px: 0.5 }}>
                                Acordos e Recebimentos
                            </MDTypography>
                        </Grid>
                        <Grid item xs={12} sx={{ pt: "12px !important" }}>
                            <AcordosRecebimentos
                                key={visao.value}
                                dados={dadosAcordos}
                                dadosAnterior={dadosAcordosAnterior}
                                atendimento={dados}
                                atendimentoAnterior={dadosAnterior}
                                cliente={clientesSel}
                                negociador={campo === "negociador" ? pessoasSel : null}
                                coordenador={campo === "coordenador" ? pessoasSel : null}
                                rotuloVariacao={rotuloVariacao}
                                dimensaoInicial={campo}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            )}

            {casos && (
                <CasosDialog
                    tipo={casos.tipo}
                    titulo={casos.titulo}
                    parametrosConsulta={parametrosConsulta}
                    clientes={clientesSel}
                    pessoas={pessoasSel}
                    campoPessoa={campo}
                    geradoEmTela={geradoEm.length ? Math.max(...geradoEm) : null}
                    onFechar={() => setCasos(null)}
                />
            )}

            <Grid item xs={12}>
                <Painel rotulo="Como os números são calculados e o que vai na planilha">
                    {[
                        visao.ajuda,
                        "Fichas acionadas e atendimentos: só o histórico de categoria Ocorrência (alterações, importações, suporte e sistema ficam fora). Fichas de honorários não entram; fichas devolvidas acionadas no período entram com saldo 0.",
                        "Negociações: acordos formalizados no período, contados para quem formalizou. Acordo refeito não conta duas vezes: na ficha com acordo vigente (em andamento ou quitado) formalizado no período, os cancelados do período ficam fora; sem vigente, conta só o último cancelado. Valor negociado = soma desses acordos; ticket médio = valor negociado ÷ negociações.",
                        "Valor recebido: pagamentos com data no período (sem a data do pagamento, a da baixa). Parcela de acordo conta para quem formalizou; baixa avulsa (sem acordo), para o responsável atual da ficha. Pagamento estornado não entra.",
                        "Conversão: das fichas acionadas no período, quantas tiveram acordo de quem as acionou (cada ficha uma vez por pessoa, mesmo com acordo refeito). No gráfico, a do mês: fichas com o 1º acordo no mês ÷ fichas acionadas no mês.",
                        "Adimplência: parcelas com vencimento no período e antes de hoje; paga = parcela com situação Pago. Parcela cancelada até o vencimento fica fora; cancelada depois conta como não paga.",
                        "Planilha: Análise de % Tempo e Base de Dados do negociador e do coordenador, Acordos e Recebimentos (uma linha por negociador, coordenador, cliente e mês), Base de Acordos e Base de Pagamentos.",
                    ].map(texto => (
                        <MDTypography key={texto} variant="caption" color="text" sx={{ display: "block" }}>
                            • {texto}
                        </MDTypography>
                    ))}
                    <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 0.5, fontStyle: "italic" }}>
                        Clientes: na carteira de cobrança, agrupados em <strong>GETNET</strong>, <strong>CIELO</strong>, <strong>PLUXEE</strong>, <strong>UMC</strong> e <strong>COBEXTRA</strong> (os demais); na carteira jurídica, o nome de cada cliente. Só os das carteiras das suas equipes e os clientes liberados para você.
                    </MDTypography>
                </Painel>
            </Grid>
        </Grid>
    );
}
